/**
 * Hooks reutilizáveis.
 *
 * Todos SSR-safe (gracefully no-op no servidor) e StrictMode-safe.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";


// ════════════════════════════════════════════════════════════════════════════
// useDebounce — retorna o valor após X ms parado
// ════════════════════════════════════════════════════════════════════════════
export function useDebounce<T>(value: T, delayMs = 200): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  return debounced;
}


// ════════════════════════════════════════════════════════════════════════════
// usePagination — controla page + perPage + cálculos derivados
// ════════════════════════════════════════════════════════════════════════════
export interface UsePaginationOptions {
  totalCount: number;
  perPage?:   number;
  initialPage?: number;
  /** Quantas páginas mostrar nos botões numerados (default 5) */
  siblingCount?: number;
}

export interface UsePaginationReturn {
  page:        number;
  perPage:     number;
  totalPages:  number;
  start:       number;          // índice 0-based (pra slicing)
  end:         number;          // índice 0-based exclusivo
  hasPrev:     boolean;
  hasNext:     boolean;
  setPage:     (p: number) => void;
  prev:        () => void;
  next:        () => void;
  /** Lista de números de páginas pra mostrar nos botões. Inclui ellipsis: [1, "...", 4, 5, 6, "...", 20]. */
  pageNumbers: (number | "ellipsis")[];
}

export function usePagination({
  totalCount, perPage = 20, initialPage = 1, siblingCount = 1,
}: UsePaginationOptions): UsePaginationReturn {
  const [page, setPageState] = useState(initialPage);
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  // Clamp se totalCount mudar
  useEffect(() => {
    if (page > totalPages) setPageState(totalPages);
  }, [page, totalPages]);

  const setPage = useCallback((p: number) => {
    setPageState(Math.max(1, Math.min(totalPages, p)));
  }, [totalPages]);

  const start = (page - 1) * perPage;
  const end   = Math.min(start + perPage, totalCount);

  const pageNumbers = useMemo<(number | "ellipsis")[]>(() => {
    const range: (number | "ellipsis")[] = [];
    const showEdges = 1;          // sempre mostra 1ª e última
    const left  = Math.max(1 + showEdges, page - siblingCount);
    const right = Math.min(totalPages - showEdges, page + siblingCount);
    range.push(1);
    if (left > 2) range.push("ellipsis");
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push("ellipsis");
    if (totalPages > 1) range.push(totalPages);
    return range;
  }, [page, totalPages, siblingCount]);

  return {
    page, perPage, totalPages, start, end,
    hasPrev: page > 1,
    hasNext: page < totalPages,
    setPage,
    prev: () => setPage(page - 1),
    next: () => setPage(page + 1),
    pageNumbers,
  };
}


// ════════════════════════════════════════════════════════════════════════════
// useLocalStorage — SSR-safe + sincroniza entre abas via storage event
// ════════════════════════════════════════════════════════════════════════════
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (v: T | ((prev: T) => T)) => void, () => void] {
  // Hidratação client-side (evita mismatch SSR)
  const [stored, setStored] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = window.localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback((v: T | ((prev: T) => T)) => {
    setStored(prev => {
      const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, JSON.stringify(next));
        }
      } catch {}
      return next;
    });
  }, [key]);

  const remove = useCallback(() => {
    try {
      if (typeof window !== "undefined") window.localStorage.removeItem(key);
    } catch {}
    setStored(initialValue);
  }, [key, initialValue]);

  // Sync entre abas
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        setStored(e.newValue !== null ? (JSON.parse(e.newValue) as T) : initialValue);
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, initialValue]);

  return [stored, setValue, remove];
}


// ════════════════════════════════════════════════════════════════════════════
// useMediaQuery — reativo a mudanças de breakpoint
// ════════════════════════════════════════════════════════════════════════════
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [query]);

  return matches;
}

// Convenience presets
export const useIsMobile     = () => useMediaQuery("(max-width: 768px)");
export const useIsTablet     = () => useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
export const useIsDesktop    = () => useMediaQuery("(min-width: 1024px)");
export const usePrefersReducedMotion = () => useMediaQuery("(prefers-reduced-motion: reduce)");


// ════════════════════════════════════════════════════════════════════════════
// useKeyboard — registra atalhos globais
// useKeyboard("k", () => open(), { meta: true })  → Cmd+K / Ctrl+K
// useKeyboard("Escape", () => close())
// ════════════════════════════════════════════════════════════════════════════
export interface KeyboardOptions {
  /** Cmd no Mac OU Ctrl no Win/Linux (default false) */
  meta?:   boolean;
  /** Shift (default false) */
  shift?:  boolean;
  /** Alt (default false) */
  alt?:    boolean;
  /** Onde escutar (default: window) */
  target?: HTMLElement | Document | Window | null;
  /** Disable temporariamente */
  enabled?: boolean;
  /** preventDefault se match (default true) */
  preventDefault?: boolean;
}

export function useKeyboard(
  key: string,
  handler: (e: KeyboardEvent) => void,
  opts: KeyboardOptions = {},
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const {
    meta = false, shift = false, alt = false,
    target, enabled = true, preventDefault = true,
  } = opts;

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const el = (target ?? window) as Window;

    const onKey = (rawEvent: Event) => {
      const e = rawEvent as KeyboardEvent;
      const wantedKey = key.toLowerCase();
      if (e.key.toLowerCase() !== wantedKey) return;

      const metaMatch  = meta  ? (e.metaKey || e.ctrlKey) : (!e.metaKey && !e.ctrlKey);
      const shiftMatch = shift ? e.shiftKey : !e.shiftKey;
      const altMatch   = alt   ? e.altKey   : !e.altKey;
      if (!metaMatch || !shiftMatch || !altMatch) return;

      if (preventDefault) e.preventDefault();
      handlerRef.current(e);
    };

    el.addEventListener("keydown", onKey as EventListener);
    return () => el.removeEventListener("keydown", onKey as EventListener);
  }, [key, meta, shift, alt, target, enabled, preventDefault]);
}


// ════════════════════════════════════════════════════════════════════════════
// useClickOutside — fecha popovers etc
// ════════════════════════════════════════════════════════════════════════════
export function useClickOutside<T extends HTMLElement>(
  handler: () => void,
  enabled: boolean = true,
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        handler();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [handler, enabled]);

  return ref;
}
